import AvPlayer from './avplayer/mediaPlayer';
import Dash from './dashjs/mediaPlayer';
import Hls from './hlsjs/mediaPlayer';
import Shaka from './shaka/mediaPlayer';
import DeviceConfig from '@devices/config';
import Modals from '@shared/types/enums/modals';
import Name from '@components/atoms/text/playerName';
import PlayerUtils from '@shared/utils/player';
import ParserUtils from '@shared/utils/parser';
import User from '@shared/classes/user';
import { IPlayerError } from '@shared/types/interfaces/error';
import { PlayerSelectorProps as Props } from '@shared/types/props/player';
import { adsSelector } from '@shared/store/slices/ads.slice';
import { closeFloatingMenu } from '@shared/store/thunks/floatingMenu.thunk';
import { initTracks } from '@shared/store/thunks/media.thunk';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import { userSelector } from '@shared/store/slices/user.slice';
import {
  PlayerError,
  PlayerState,
  PlayerType,
} from '@shared/types/enums/player';
import {
  clearError,
  mediaSelector,
  setError,
} from '@shared/store/slices/media.slice';
import {
  clearModal,
  modalSelector,
  setModal,
} from '@shared/store/slices/modal.slice';

const DISPLAY_NAME = false;
const ERROR_TIMEOUT = 3000;

const MediaPlayer = {
  shaka: Shaka,
  dashjs: Dash,
  hlsjs: Hls,
  avplayer: AvPlayer,
};

function PlayerSelector({ asset, channel }: Props) {
  const dispatch = useAppDispatch();
  const { videoAd } = useAppSelector(adsSelector);
  const { error, ready, state } = useAppSelector(mediaSelector);
  const { current: modal } = useAppSelector(modalSelector);
  const { freemiumFilter, initialized, planId } = useAppSelector(userSelector);
  const [name, setName] = useState('Dash');
  const [source, setSource] = useState('');
  const [Player, setPlayer] = useState(() => Dash);

  const errorRef = useRef(error);
  const modalRef = useRef(modal);
  const stateRef = useRef(state);

  errorRef.current = error;
  modalRef.current = modal;
  stateRef.current = state;

  // Memoized values

  const headers = useMemo(() => PlayerUtils.getContentHeaders(), []);
  const { drm, bookmark, keepWatching, start, end } = useMemo(() => {
    const drm = asset
      ? PlayerUtils.getVodDrm(asset)
      : PlayerUtils.getChannelDrm(channel);
    const bookmark = asset?.startBookmark || undefined;
    const keepWatching = asset?.keepWatchingMark || undefined;
    const start = asset?.start || undefined;
    const end = asset?.endBookmark || undefined;
    return { drm, bookmark, keepWatching, start, end };
  }, [asset, channel]);

  // Effect hooks

  useEffect(() => {
    if ((!asset && !channel) || !initialized) return;
    const { dash, free, hls } = asset?.streams || channel?.streams || {};
    const { retry } = error || {};
    // Check for source availability
    if (!dash && !hls && !error?.type) {
      const error: IPlayerError = {
        detail: 'Empty source',
        player: PlayerType.DASHJS,
        type: PlayerError.SOURCE,
      };
      dispatch(setError(error));
      setSource('');
      return;
    }

    const isCBCS = channel?.avplay ?? asset?.channel?.avplay ?? false;
    const isFree = freemiumFilter && User.getPlan(planId)?.isFree;
    const source = (isFree && free) || dash || hls || '';

    // Set player based on playback attempt

    setPlayer(() => {
      let DashPlayer = Dash;
      let HLSPlayer = Hls;
      let FallbackPlayer = Shaka;

      // Set default dash player
      if (DeviceConfig.defaultDashPlayer) {
        switch (DeviceConfig.defaultDashPlayer) {
          case PlayerType.AVPLAYER:
            if (isCBCS) {
              DashPlayer = AvPlayer;
            }
            break;
          case PlayerType.SHAKA:
            DashPlayer = Shaka;
            break;
        }
      }

      // Set default hls player
      if (DeviceConfig.defaultHLSPlayer) {
        switch (DeviceConfig.defaultHLSPlayer) {
          case PlayerType.AVPLAYER:
            HLSPlayer = AvPlayer;
            break;
          case PlayerType.SHAKA:
            HLSPlayer = Shaka;
            break;
        }
      }

      // Set default fallback player
      if (DeviceConfig.defaultFallbackPlayer) {
        switch (DeviceConfig.defaultFallbackPlayer) {
          case PlayerType.AVPLAYER:
            FallbackPlayer = AvPlayer;
            break;
        }
      }

      const player =
        (retry || 0) % 2 ? FallbackPlayer : dash ? DashPlayer : HLSPlayer;

      if (player === MediaPlayer.avplayer) setName('AVPlayer');
      else if (player === MediaPlayer.dashjs) setName('DashJS');
      else if (player === MediaPlayer.hlsjs) setName('Hls.js');
      else if (player === MediaPlayer.shaka) setName('Shaka');
      else setName('');

      return player;
    });
    setSource(() => {
      return ParserUtils.sourceParser(source);
    });
  }, [asset, channel, error, initialized]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (
        (errorRef.current.code || errorRef.current.detail) &&
        !modalRef.current &&
        stateRef.current === PlayerState.ERROR
      ) {
        dispatch(closeFloatingMenu());
        dispatch(setModal(Modals.PLAYER_ERROR));
      }
    }, ERROR_TIMEOUT);
    return () => clearTimeout(timeout);
  }, [error, modal, state]);

  useEffect(() => {
    if (modal === Modals.PLAYER_ERROR && state !== PlayerState.ERROR) {
      dispatch(clearError());
      dispatch(clearModal());
    }
  }, [modal, state]);

  useEffect(() => {
    if (ready) {
      dispatch(initTracks(asset?.id || channel?.id || ''));
    }
  }, [ready, asset, channel]);

  useEffect(() => {
    return () => {
      dispatch(clearError());
      modalRef.current === Modals.PLAYER_ERROR && dispatch(clearModal());
    };
  }, []);

  return !videoAd && source ? (
    <>
      <Player
        asset={asset}
        channel={channel}
        drm={drm}
        headers={headers}
        position={{ bookmark, keepWatching, start, end }}
        source={source}
      />
      {DISPLAY_NAME ? <Name>{name}</Name> : null}
    </>
  ) : null;
}

export { MediaPlayer };
export default PlayerSelector;
